/* eslint-disable react/prop-types */
import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { FixedSizeList, areEqual } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import AutoSizer from 'react-virtualized-auto-sizer'

import { ArrowLeft, ArrowRight } from '../../../../../component'
import WorkoutCard from '../workout-card/workout-card'

import CRUD from '../../../../../api/generic/crud/crud'
import URL, { replaceUrl } from '../../../../../config/url'

import style from './workout-scroll.module.scss'

/**
 * Number of items that will be preloaded
 */
const TOTAL_ITEMS_PER_PAGE = 5
const CARD_WIDTH = 310

/**
 * Get the item for the list. This list is kept in the following format
 * list = { [page]: list of items }
 * @param {Object} list Object with list of items of workouts
 * @param {Number} index Index on the list. This value is relative the the complete list
 */
export const getItem = (list, index) => {
    const page = parseInt(index / TOTAL_ITEMS_PER_PAGE, 10)
    const indexInternal = index % TOTAL_ITEMS_PER_PAGE

    return _.get(list, `${page}.${indexInternal}`)
}

/**
 * Render Arrow to manipulate scroller
 * @param {Node} Arrow Arrow element
 * @param {Ref} workoutListRef Ref to the workout list
 * @param {Number} ignoreIndex Element case (first or last on the list)
 * @param {function} getTargetEl Function to find the next element
 */
export const renderArrow = (Arrow, workoutListRef, ignoreIndex, totalItems) => {
    const width = _.get(workoutListRef, 'current._listRef.props.width')
    const nrCardsVisible = width
        ? parseInt(width / CARD_WIDTH, 10)
        : TOTAL_ITEMS_PER_PAGE - 1

    if (nrCardsVisible >= totalItems) {
        return <div />
    }

    return (
        <Arrow
            className="hidden-xs hidden-sm"
            onClick={() => {
                const widthScroll = _.get(
                    workoutListRef,
                    'current._listRef.state.scrollOffset'
                )
                const currentItem = parseInt(
                    (widthScroll || 0) / CARD_WIDTH,
                    10
                )

                if (currentItem !== ignoreIndex) {
                    let targetEl
                    if (ignoreIndex === 0) {
                        // not < 0
                        targetEl = currentItem - nrCardsVisible
                        targetEl = Math.max(targetEl, 0)
                    } else {
                        // not > than totalElements
                        targetEl = currentItem + nrCardsVisible
                        targetEl =
                            targetEl <= ignoreIndex ? targetEl : ignoreIndex
                    }

                    workoutListRef.current._listRef.scrollToItem(
                        targetEl,
                        'start'
                    )
                }
            }}
        />
    )
}

export const WorkoutScroll = ({ programId, workouts, workoutImages }) => {
    const workoutListRef = useRef(null)

    /* Manages workout items */
    let workoutList = {
        0: workouts.content,
    }

    /**
     * Load from backend the items
     * @param {Number} page The page that should be loaded from the backend
     */
    const getPage = async index => {
        const page = parseInt(index / TOTAL_ITEMS_PER_PAGE, 10)
        if (workoutList[page] !== null) {
            /* Avoid calling API for the same page */
            workoutList[page] = null

            /* Get page */
            const result = await CRUD.get(
                replaceUrl(
                    URL.workoutsOfProgram,
                    programId,
                    page,
                    TOTAL_ITEMS_PER_PAGE
                )
            )

            /* Set workoutlist */
            workoutList = {
                ...workoutList,
                [page]: result && result.data.content,
            }
        }
    }

    /**
     * For each item, get respective page. Optimization to avoid
     * quering same page is defined inside getPage function
     * @param {Number} startIndex
     * @param {Number} stopIndex
     */
    const loadMoreItems = async (startIndex, stopIndex) => {
        _.range(startIndex, stopIndex + 1).map(index => getPage(index))
    }

    /**
     * Render Workout card. Memo it to avoid rerendering
     */
    const CardMemoized = React.memo(
        ({ index, style: styleEl, workout }) => (
            <WorkoutCard
                index={index}
                styleEl={styleEl}
                workoutImage={workoutImages[index]}
                workout={workout}
            />
        ),
        areEqual
    )

    return (
        <div className={style.workoutsWrapper}>
            {renderArrow(ArrowLeft, workoutListRef, 0, workouts.totalElements)}
            <div className={style.workoutScroller}>
                <AutoSizer>
                    {({ width, height }) => (
                        <InfiniteLoader
                            isItemLoaded={index => getItem(workoutList, index)}
                            itemCount={workouts.totalElements}
                            loadMoreItems={loadMoreItems}
                            threshold={0}
                            minimumBatchSize={TOTAL_ITEMS_PER_PAGE}
                            ref={workoutListRef}
                        >
                            {({ onItemsRendered, ref }) => (
                                <FixedSizeList
                                    className={style.fixedSizeList}
                                    layout="horizontal"
                                    height={height}
                                    itemCount={workouts.totalElements}
                                    itemSize={CARD_WIDTH}
                                    overscanCount={0}
                                    width={width}
                                    onItemsRendered={onItemsRendered}
                                    ref={ref}
                                >
                                    {
                                        /**
                                         * Render Card
                                         */
                                        params => (
                                            <CardMemoized
                                                workout={getItem(
                                                    workoutList,
                                                    params.index
                                                )}
                                                {...params}
                                            />
                                        )
                                    }
                                </FixedSizeList>
                            )}
                        </InfiniteLoader>
                    )}
                </AutoSizer>
            </div>
            {renderArrow(
                ArrowRight,
                workoutListRef,
                workouts.totalElements,
                workouts.totalElements
            )}
        </div>
    )
}

WorkoutScroll.propTypes = {
    programId: PropTypes.number.isRequired,
    workoutImages: PropTypes.arrayOf(PropTypes.object).isRequired,
    workouts: PropTypes.object.isRequired,
}

export default WorkoutScroll
